import { useContext, useState } from "react";
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { AuthContext } from "../../context/user.context";

/**
 * WEB-004: selector de unidad para usuarios multisedes.
 * El backend ya filtró por aplicación; aquí solo se elige la unidad activa.
 */
const UnitSelector = () => {
  const { availableWorkspaces, activeContext, selectUnit, requestSignOut } =
    useContext(AuthContext);
  const [selectingId, setSelectingId] = useState(null);
  const [error, setError] = useState(null);
  const isGroupSelector = activeContext?.activeApp === "group_web";

  const handleSelect = async (id) => {
    setSelectingId(id);
    setError(null);
    try {
      await selectUnit(id);
    } catch (selectionError) {
      setError(
        selectionError?.message ||
          "No pudimos validar la selección. Reintentá.",
      );
    } finally {
      setSelectingId(null);
    }
  };

  return (
    <Container
      component="main"
      maxWidth="sm"
      sx={{ py: 6 }}
      aria-labelledby="workspace-selector-title"
    >
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography
          id="workspace-selector-title"
          variant="h4"
          sx={{ fontWeight: 700, mb: 1 }}
        >
          {isGroupSelector ? "Seleccioná tu grupo" : "Seleccioná tu sucursal"}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Tenés acceso a más de una opción. Elegí en cuál querés operar.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} role="alert">
          {error}
        </Alert>
      )}

      {availableWorkspaces.map((unit) => (
        <Card
          key={unit.id}
          elevation={0}
          sx={{ mb: 2, border: "1px solid", borderColor: "divider" }}
        >
          <CardContent
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <StorefrontIcon color="primary" />
              <Box>
                <Typography variant="h6">
                  {unit.displayName || unit.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isGroupSelector
                    ? "Grupo"
                    : unit.kind === "head_office"
                      ? "Casa central"
                      : "Sucursal"}
                  {unit.role ? ` · ${unit.role}` : ""}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={() => handleSelect(unit.id)}
              disabled={selectingId !== null}
              aria-label={`Entrar a ${unit.displayName || unit.id}`}
            >
              {selectingId === unit.id ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Entrar"
              )}
            </Button>
          </CardContent>
        </Card>
      ))}

      {availableWorkspaces.length === 0 && (
        <Alert severity="warning">
          Ya no hay sucursales disponibles para esta aplicación.
        </Alert>
      )}

      <Box sx={{ textAlign: "center", mt: 3 }}>
        <Button color="inherit" onClick={requestSignOut}>
          Cerrar sesión
        </Button>
      </Box>
    </Container>
  );
};

export default UnitSelector;
